import React, { useMemo } from 'react';
import { BentoCard } from '../BentoCard';
import { CategoryVocabularyMissingNotice } from '../CategoryVocabularyMissingNotice';
import { SkeletonBlock } from '../SkeletonBlock';
import { theme } from '../../theme';
import type { PrimaryAxisVolumeBin } from '../../serverTypes';
import { useCategoryMeta } from '../../context/ClassificationSchemaContext';
import { volumeStackKeys } from './charts/_volumeStack';
import { failedWord, unclassifiedWord } from './residueWords';

// The residue block: a hairline, then one centred column per residue present; it always reserves one list-row height.
const RESIDUE_BLOCK_STYLE: React.CSSProperties = {
  borderTop: `1px solid ${theme.colors.bgLight}`, marginTop: 9, paddingTop: 9, minHeight: 18,
  display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', columnGap: 24,
};
const RESIDUE_COLUMN_STYLE: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, animation: 'stx-fadeIn 0.3s ease',
};
const SWATCH_BASE: React.CSSProperties = { flexShrink: 0, width: 10, height: 10, borderRadius: 2, boxSizing: 'border-box' };

// <purpose>Per-category volume summary — sorted horizontal bar list showing total count per
// category for the active filter window. Replaces the old ECharts cumulative-line hero that
// suffered label collisions and hover dead-zones on VNB's 9 tie-prone marktprozess ids.</purpose>
// <dependencies>perPrimaryAxisVolumeByPeriod DTO field, useCategoryMeta resolver, BentoCard, theme</dependencies>
// <constraints>
//   VOLUME / RECONCILIATION surface — part 7 round 5, 2026-10-14 (operator-accepted screen, superseding the round-4
//   residue lines): every received mail is in exactly one of three classes — classified rows · unklassifiziert ·
//   fehlgeschlagen = Eingegangen (stats.totalReceived). Only the classified ones are rows (keys = cm.categoryKeys
//   minus the unclassified value); the residue is NEVER a row or a bar: under a hairline it is N centred grid
//   columns, one per residue PRESENT (count > 0) — swatch · word · count in the swatch's hue (Fehler: filled
//   theme.statusStrip.error; unclassified: white, outlined theme.statusStrip.unclassified). No divider.
//   An undefined count (older backend / pending) is absent, never derived. The block ALWAYS reserves one list-row
//   height (both absent = empty block, same height); columns fade in (stx-fadeIn); the skeleton reserves the same block.
//   Words only via ./residueWords (shared with the Ungeklärt KPI sub-line and the volume charts): the served
//   unclassified face, or the chrome fallback for a vocabulary that declares none (WIM); STATUS_LABELS for Fehler.
//   The categorization-performance surfaces (Kategorieverteilung, rates) use performanceCategoryKeys.
//   Sort: descending count; ties broken alphabetically by label (deterministic, no collision).
//   Labels via cm.label(key) — never raw ids.
//   Rows are <li> buttons for accessibility.
// </constraints>

type Props = {
  data: PrimaryAxisVolumeBin[];
  filterHash?: string;
  /** stats.totalWithoutPrimaryAxisCount (the page's class: not failed, no real category) — the unclassified residue column;
   *  never stats.totalUnclassifiedCount (the export's class). */
  withoutPrimaryAxisTotal?: number;
  /** stats.totalFehlgeschlagenCount — the failed residue column. */
  failedTotal?: number;
};

export const VolumenJeKategorieCard: React.FC<Props> = ({ data, withoutPrimaryAxisTotal, failedTotal }) => {
  const cm = useCategoryMeta();

  // Aggregate total count per category key across all bins.
  // part 7 round 2, 2026-10-13: the unclassified value is no row here (see <constraints>) — a row whose
  // category is not in the tenant's axis at all is dropped by the knownKeys filter below, and the
  // unclassified value by volumeStackKeys; the residue columns carry it.
  const rows = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const b of data) {
      totals[b.primaryAxis] = (totals[b.primaryAxis] ?? 0) + b.count;
    }
    // Keep only keys present in data that the tenant's axis knows about (union of data + axis keys).
    // Also include any key the axis exposes but with zero count (so gMSB always shows K0–K4).
    const stackKeys = volumeStackKeys(cm);
    const knownKeys = new Set(stackKeys);
    const dataKeys = Object.keys(totals).filter(k => knownKeys.has(k));
    // Include axis keys not in data at count 0 so the full legend is always shown.
    const allKeys = Array.from(new Set([...dataKeys, ...stackKeys]));

    return allKeys
      .map(key => ({ key, label: cm.label(key), count: totals[key] ?? 0 }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      });
  }, [data, cm]);

  const maxCount = rows[0]?.count ?? 0;
  // Residue columns: only a residue that is served AND non-zero gets one (see <constraints>).
  const residueItems: Array<{ key: string; word: string; count: number; hue: string; swatch: React.CSSProperties }> = [];
  if (failedTotal !== undefined && failedTotal > 0) {
    residueItems.push({ key: 'failed', word: failedWord(), count: failedTotal, hue: theme.statusStrip.error,
      swatch: { background: theme.statusStrip.error } });
  }
  if (withoutPrimaryAxisTotal !== undefined && withoutPrimaryAxisTotal > 0) {
    residueItems.push({ key: 'unclassified', word: unclassifiedWord(cm, true) ?? '', count: withoutPrimaryAxisTotal,
      hue: theme.statusStrip.unclassified,
      swatch: { background: theme.colors.white, border: `1.5px solid ${theme.statusStrip.unclassified}` } });
  }

  // Task C loading posture: the title itself glues cm.categoryNoun ("Volumen je Kategorie") —
  // untrustworthy while /config is still resolving (cm.configLoading), same placeholder-floor
  // risk as every other vocab-derived accessor below. Checked FIRST, ahead of vocabularyMissing
  // and the data-length empty state, since role/schema/vocab aren't resolved enough yet to trust
  // either of those branches' own conditions.
  if (cm.configLoading) {
    // Row count MUST track the real content's row count, not a fixed guess — a hardcoded 5 reads
    // fine for gMSB's 5 K-codes but undershoots the double-digit VNB marktprozess set, so the card
    // would grow taller the instant real data replaces the skeleton (UI Layout Stability Rule
    // violation). Reuses `rows` (computed above, unconditionally, every render) rather than
    // re-deriving categoryKeys a second time — it is ALREADY the exact allKeys the loaded branch
    // below renders, including any out-of-vocab data keys, so this can only
    // ever match the real footprint, never merely approximate it. `rows` is empty (not merely
    // short) on a genuine cold boot — categoryKeys hasn't resolved either yet — so 5 is only that
    // cold-boot fallback.
    const skeletonRowCount = rows.length || 5;
    return (
      <BentoCard title={<SkeletonBlock width={140} height={14} />}>
        <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: skeletonRowCount }, (_, i) => 90 - (i % 4) * 15).map((pct, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SkeletonBlock width={10} height={10} borderRadius={2} />
              <SkeletonBlock width={140} height={12} />
              <SkeletonBlock width={`${pct}%`} height={8} borderRadius={3} style={{ flex: 1 }} />
              <SkeletonBlock width={28} height={12} />
            </li>
          ))}
        </ul>
        <div data-testid="volumen-residue" style={RESIDUE_BLOCK_STYLE}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
            <SkeletonBlock width={10} height={10} borderRadius={2} />
            <SkeletonBlock width={90} height={12} />
          </div>
        </div>
      </BentoCard>
    );
  }

  // Role-configuration gap (no schema/vocabulary for a KNOWN non-legacy role) takes precedence
  // over the data-volume empty state below — otherwise `rows` silently comes out [] regardless of
  // `data.length` (every dataKey gets filtered out by an empty knownKeys set) and the card would
  // render an empty <ul> with no message at all, worse than a misleading "Keine Daten".
  if (cm.vocabularyMissing) {
    return (
      <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
        <CategoryVocabularyMissingNotice />
      </BentoCard>
    );
  }

  // Show empty state when there are no incoming data bins at all (not just all-zero schema keys).
  if (data.length === 0) {
    return (
      <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
        <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
          Keine Daten
        </div>
      </BentoCard>
    );
  }

  return (
    <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
      <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(({ key, label, count }) => {
          const color = cm.color(key);
          const barWidthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
          return (
            <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Color swatch */}
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: color,
                }}
              />
              {/* Label */}
              <span
                style={{
                  flexShrink: 0,
                  width: 140,
                  fontSize: 12,
                  color: theme.colors.textSecondary,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
                title={label}
              >
                {label}
              </span>
              {/* Bar track */}
              <span
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 3,
                  background: theme.colors.bgLight,
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${barWidthPct}%`,
                    borderRadius: 3,
                    background: color,
                    // Transition width for smooth re-renders on filter change.
                    transition: 'width 300ms ease',
                  }}
                />
              </span>
              {/* Count */}
              <span
                style={{
                  flexShrink: 0,
                  width: 28,
                  textAlign: 'right',
                  fontSize: 12,
                  fontWeight: 600,
                  color: count > 0 ? color : theme.colors.textSecondary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {count}
              </span>
            </li>
          );
        })}
      </ul>
      <div data-testid="volumen-residue" style={RESIDUE_BLOCK_STYLE}>
        {residueItems.map(({ key, word, count, hue, swatch }) => (
          <div key={key} data-residue={key} style={RESIDUE_COLUMN_STYLE}>
            <span aria-hidden="true" style={{ ...SWATCH_BASE, ...swatch }} />
            <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>{word}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: hue, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
          </div>
        ))}
      </div>
    </BentoCard>
  );
};
